/**
 * Centralized Mobile Styles Constants
 * 
 * This file contains consistent mobile styling classes for fonts, buttons, 
 * padding, and other UI elements to ensure consistency across the application.
 * 
 * Usage:
 * import { mobileStyles } from '../constants/mobileStyles';
 * 
 * className={mobileStyles.text.heading}
 * className={mobileStyles.button.primary}
 */

export const mobileStyles = {
  // Text/Font Sizes
  text: {
    // Headings
    h1: 'text-2xl sm:text-3xl lg:text-4xl font-bold',
    h2: 'text-xl sm:text-2xl lg:text-3xl font-bold',
    h3: 'text-lg sm:text-xl lg:text-2xl font-semibold',
    h4: 'text-base sm:text-lg lg:text-xl font-semibold',
    h5: 'text-sm sm:text-base lg:text-lg font-semibold',
    
    // Body text
    body: 'text-sm sm:text-base',
    bodySmall: 'text-xs sm:text-sm',
    bodyLarge: 'text-base sm:text-lg',
    
    // Labels and captions
    label: 'text-xs sm:text-sm font-medium',
    caption: 'text-xs sm:text-sm text-gray-600',
    small: 'text-xs',
    
    // Truncate and overflow
    truncate: 'truncate',
    breakWords: 'break-words',
  },

  // Buttons
  button: {
    // Primary button
    primary: 'px-3 sm:px-4 lg:px-6 py-1.5 sm:py-2 lg:py-2.5 text-xs sm:text-sm lg:text-base font-semibold rounded-lg transition-colors whitespace-nowrap',
    primaryFull: 'w-full sm:w-auto px-3 sm:px-4 lg:px-6 py-1.5 sm:py-2 lg:py-2.5 text-xs sm:text-sm lg:text-base font-semibold rounded-lg transition-colors',
    
    // Secondary button
    secondary: 'px-3 sm:px-4 lg:px-6 py-1.5 sm:py-2 lg:py-2.5 text-xs sm:text-sm lg:text-base font-medium rounded-lg border transition-colors whitespace-nowrap',
    
    // Icon button
    icon: 'p-1.5 sm:p-2 rounded-lg transition-colors',
    iconSmall: 'p-1 sm:p-1.5 rounded-lg transition-colors',
    
    // Small button
    small: 'px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors',
    
    // Link button
    link: 'text-xs sm:text-sm font-medium text-cc_brand hover:underline',
  },

  // Padding
  padding: {
    // Container padding
    container: 'p-2 sm:p-4 lg:p-6',
    containerX: 'px-2 sm:px-4 lg:px-6',
    containerY: 'py-2 sm:py-4 lg:py-6',
    
    // Section padding
    section: 'p-3 sm:p-4 lg:p-5',
    sectionX: 'px-3 sm:px-4 lg:px-5',
    sectionY: 'py-3 sm:py-4 lg:py-5',
    
    // Card padding
    card: 'p-3 sm:p-4',
    cardX: 'px-3 sm:px-4',
    cardY: 'py-3 sm:py-4',
    
    // Small padding
    small: 'p-2 sm:p-3',
    smallX: 'px-2 sm:px-3',
    smallY: 'py-2 sm:py-3',
  },

  // Spacing/Gaps
  gap: {
    small: 'gap-1.5 sm:gap-2',
    medium: 'gap-2 sm:gap-3 lg:gap-4',
    large: 'gap-3 sm:gap-4 lg:gap-6',
    xlarge: 'gap-4 sm:gap-6 lg:gap-8',
  },

  // Margins
  margin: {
    bottom: {
      small: 'mb-2 sm:mb-3',
      medium: 'mb-3 sm:mb-4',
      large: 'mb-4 sm:mb-6',
    },
    top: {
      small: 'mt-2 sm:mt-3',
      medium: 'mt-3 sm:mt-4',
      large: 'mt-4 sm:mt-6',
    },
  },

  // Tabs
  tabs: {
    container: 'flex overflow-x-auto border-b border-gray-200 bg-gray-50',
    tab: 'flex-1 min-w-[100px] sm:min-w-0 px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap',
    tabActive: 'bg-white text-cc_brand border-b-2 border-cc_brand',
    tabInactive: 'bg-gray-50 text-gray-700 hover:bg-gray-100',
  },

  // Input fields
  input: {
    base: 'w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-cc_brand focus:border-transparent',
    search: 'w-full px-3 sm:px-4 py-1.5 sm:py-2 pl-8 sm:pl-10 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-cc_brand focus:border-transparent',
  },

  // Cards
  card: {
    base: 'bg-white rounded-lg shadow border border-gray-200',
    padding: 'p-3 sm:p-4',
  },

  // Grid layouts
  grid: {
    responsive: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6',
    responsive2: 'grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4',
    responsive4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4',
  },

  // Flex layouts
  flex: {
    row: 'flex flex-col sm:flex-row',
    rowReverse: 'flex flex-col-reverse sm:flex-row',
    center: 'flex items-center justify-center',
    between: 'flex flex-col sm:flex-row justify-between items-start sm:items-center',
    start: 'flex flex-col sm:flex-row items-start sm:items-center',
  },

  // Icons
  icon: {
    small: 'w-4 h-4 sm:w-5 sm:h-5',
    medium: 'w-5 h-5 sm:w-6 sm:h-6',
    large: 'w-6 h-6 sm:w-8 sm:h-8',
  },

  // Modal/Dialog
  modal: {
    container: 'w-[calc(100%-32px)] sm:w-[90vw] md:w-[500px] lg:w-[650px] max-w-full',
    padding: 'p-4 sm:p-6',
    header: 'text-lg sm:text-xl lg:text-2xl font-semibold',
  },

  // Badge/Chip
  badge: {
    base: 'px-2 sm:px-2.5 py-0.5 sm:py-1 text-xs sm:text-sm font-medium rounded-full',
    small: 'px-1.5 py-0.5 text-xs font-medium rounded-full',
  },

  // Scrollable containers
  scrollable: {
    horizontal: 'overflow-x-auto',
    vertical: 'overflow-y-auto',
    both: 'overflow-auto',
    maxHeight: {
      small: 'max-h-[200px] sm:max-h-[300px]',
      medium: 'max-h-[400px] sm:max-h-[500px]',
      large: 'max-h-[60vh] sm:max-h-[80vh]',
    },
  },
} as const;

/**
 * Helper function to combine mobile styles with custom classes
 * Usage: cn(mobileStyles.button.primary, 'bg-cc_brand text-white')
 */
import { cn as cnUtil } from '../../../lib/utils';
export const cn = cnUtil;

